// @flow

import type { ProductPack } from "../product-pricing/productPricingTypes";
import {
  getCmpPrice,
  getPrice,
  isValidPrice,
} from "../product-pricing/updateDisplayedPrices";
import { getQuantity, isValidQuantity } from "../quantity";
import { getSelectedUom } from "./uom";
import { isMaxUser } from "../utils";
import {
  getCartButton,
  getCreativeGlassButton,
  getEnquireOnlyButton,
  getInstoreOnlyButton,
  getShopInMaxButton,
  getTradeAccountOnlyButton,
} from "./add-to-cart-buttons";
import { submitV2AnalyticsEvent } from "../utils/analyticsUtils";
import { getCookieUserId } from "../utils/cookieUtils";

const buttonELement = (product: HTMLElement): HTMLButtonElement => {
  return ((product.querySelector(`add-to-cart`): any): HTMLButtonElement);
};

const tradeAccountHoldersOnlyText = (product: HTMLElement): HTMLElement => {
  return ((product.querySelector(
    `.product-tile__section-trade-only-prompt`,
  ): any): HTMLElement);
};

const customPromtTextContainer = (product: HTMLElement): HTMLElement => {
  return ((product.querySelector(
    `.product-tile__section-custom-prompt`,
  ): any): HTMLElement);
};

const customPromtText = (product: HTMLElement): HTMLElement => {
  return ((product.querySelector(
    `.product-tile__section-custom-prompt .custom-prompt`,
  ): any): HTMLElement);
};

const quantityElement = (product: HTMLElement): HTMLElement => {
  return ((product.querySelector(
    `.product-tile__switch-group--qty`,
  ): any): HTMLElement);
};

const getSecureUrl = (product: HTMLElement): string => {
  const ele = product.querySelector(`.product_tile__action-cart-actions`);
  let secureUrl;

  if (ele && ele.attributes.length > 0 && ele.hasAttribute("data-secure-url"))
    secureUrl = ele.getAttribute("data-secure-url");

  return secureUrl ? secureUrl : "";
};

const getProductUrl = (product: HTMLElement): string => {
  const ele = product.querySelector(`.product_tile__action-cart-actions`);
  let productUrl;

  if (ele && ele.attributes.length > 0 && ele.hasAttribute("data-product-url"))
    productUrl = ele.getAttribute("data-product-url");

  return productUrl ? productUrl : "";
};

const setCartButtonPrice = (product: HTMLElement, pack: ProductPack) => {
  let price = getPrice(product, pack);
  const cmp = getCmpPrice(product, pack);
  buttonELement(product).dataset.gstInclusivePrice = price.toString();
  buttonELement(product).dataset.cmpGstInclusivePrice = cmp.toString();
};

const setCartButtonQuantity = (product: HTMLElement) => {
  let quantity = getQuantity(product);
  buttonELement(product).dataset.quantity = quantity;
};

const setCartButtonActiveUnit = (product) => {
  buttonELement(product).dataset.unit = getSelectedUom(product);
};

const enableAddButton = (product) => {
  buttonELement(product).disabled = false;
};
const disableAddButton = (product) => {
  buttonELement(product).disabled = true;
};

const showTradeAccountHoldersOnlyText = (product) => {
  let el = tradeAccountHoldersOnlyText(product);
  if (el) {
    el.classList.remove("hidden");
  }
};

const showCustomPromptText = (product, promptText) => {
  let el = customPromtTextContainer(product);
  if (el) {
    el.classList.remove("hidden");
  }
  let text = customPromtText(product);
  if (text) {
    text.textContent = promptText;
  }
};

const showQuantity = (product) => {
  const qtyElement = quantityElement(product);
  qtyElement.style.visibility = "visible";

  // Also ensure the parent switches section is visible
  const switchesSection = product.querySelector(
    `.product-tile__section-switches`,
  );
  if (switchesSection) {
    switchesSection.style.display = "block";
    switchesSection.style.visibility = "visible";
  }
};

const hideQuantity = (product) => {
  const qtyElement = quantityElement(product);
  qtyElement.style.visibility = "hidden";
};

const shouldEnableCartButton = (product, pack: ProductPack) => {
  const hasValidPrice = isValidPrice(product, pack);
  const hasValidQuantity = isValidQuantity(product);
  return hasValidPrice && hasValidQuantity;
};

export const setButtonEnabledState = (
  product: HTMLElement,
  pack: ProductPack,
) => {
  const shouldEnable = shouldEnableCartButton(product, pack);

  if (shouldEnable) {
    setCartButtonQuantity(product);
    setCartButtonActiveUnit(product);
    setCartButtonPrice(product, pack);
    showQuantity(product);
    enableAddButton(product);
  } else {
    disableAddButton(product);
    hideQuantity(product);
  }
};

const setCartActionElement = (
  product: HTMLElement,
  cartAction: HTMLElement,
) => {
  try {
    const cartActionElement = product.querySelector(
      `.product_tile__action-cart-actions`,
    );

    if (cartActionElement && cartActionElement.parentElement) {
      cartActionElement.parentElement.appendChild(cartAction);
      cartActionElement.remove();
    }
  } catch (e) {
    console.error(e);
  }
};

const getCartButtonAttributes = (product: HTMLElement) => {
  try {
    const cartActionElement = product.querySelector(
      `.product_tile__action-cart-actions`,
    );

    if (cartActionElement && cartActionElement.attributes.length > 0) {
      return cartActionElement.attributes;
    }
  } catch (e) {
    console.error(e);
  }
};

function dispatchAddProductToCartAnalytics(
  productNumber,
  productName,
  itemQuantity,
  itemValue,
  stockAvailability,
  augmented,
  rank,
) {
  const isPunchout =
    window.location.search.indexOf("punchout=true") === -1 ? "No" : "Yes";
  const wrapper = document.querySelector(".product-search-page-body-wrapper");
  const pageNumber = wrapper ? wrapper.dataset.pageNumber : null;
  const searchTerm = new URLSearchParams(window.location.search).get("query");

  submitV2AnalyticsEvent("Add Product to Cart", {
    originID: null,
    origin: "PLP",
    originType: null,
    productCode: productNumber,
    productName,
    lineItemCount: 1,
    itemQuantity,
    itemValue,
    isPunchout,
    stockAvailability,
    augmented,
    rank,
    pageNumber,
    searchTerm,
  });
}
function dispatchAnalyticsOnClick(addButton, productTile) {
  addButton.addEventListener("click", function () {
    const { quantity, unit, productNumber, productName, augmented } =
      addButton.dataset;
    const { pack1MypriceIncGst, pack2MypriceIncGst, stockAvailability, rank } =
      productTile.dataset;
    const pack: ProductPack = unit === "1" || unit === "ONE" ? "1" : "2";

    let totalValue;
    if (isMaxUser(window)) {
      const price = pack === "1" ? pack1MypriceIncGst : pack2MypriceIncGst;
      totalValue = price
        ? parseFloat(parseFloat(price) * parseFloat(quantity)).toFixed(2)
        : 0;
    } else {
      // Non-trade users never have a customer ("my price") price populated — they only ever see CMP/RRP.
      const cmpPrice = getCmpPrice(productTile, pack);
      totalValue =
        cmpPrice >= 0
          ? parseFloat(cmpPrice * parseFloat(quantity)).toFixed(2)
          : 0;
    }

    if (augmented && augmented === "true") {
      storeAugmentedProductInSessionStorage(productNumber);
    }

    dispatchAddProductToCartAnalytics(
      productNumber,
      productName,
      quantity,
      parseFloat(totalValue),
      stockAvailability,
      augmented,
      rank,
    );
  });
}

function storeAugmentedProductInSessionStorage(productNumber) {
  const key = `augmentedSearch_${getCookieUserId()}`;
  const augmentedSearchValues = sessionStorage.getItem(key);
  if (augmentedSearchValues) {
    let productNumbers = JSON.parse(augmentedSearchValues);
    if (productNumbers && !productNumbers.includes(productNumber)) {
      productNumbers.push(productNumber);
      sessionStorage.setItem(key, JSON.stringify(productNumbers));
    }
  } else {
    const productNumbers = [];
    productNumbers.push(productNumber);
    sessionStorage.setItem(key, JSON.stringify(productNumbers));
  }
}
export const initCartButtonState = (win: { document: Document }) => {
  try {
    const products = [...win.document.querySelectorAll(`.product-tile`)];
    const wrapper = win.document.querySelector(
      `.product-search-page-body-wrapper`,
    );
    const userType = wrapper ? wrapper.getAttribute("data-user-type") : null;

    products.forEach((product: HTMLElement) => {
      let actionEle;

      let productPurchsability =
        product.getAttribute("data-cta") || "Shop in maX";
      if (userType === "MAX_USER") {
        if (
          productPurchsability &&
          productPurchsability === "Check Availability (Creative Glass)"
        ) {
          showCustomPromptText(product, "Available in selected areas only");
          actionEle = getCreativeGlassButton(productPurchsability);
        } else {
          const attributes = getCartButtonAttributes(product);
          if (attributes) {
            actionEle = getCartButton(attributes, productPurchsability);
            if (actionEle) {
              dispatchAnalyticsOnClick(actionEle, product);
            }
          }
        }
      } else {
        if (
          productPurchsability &&
          productPurchsability === "Check Availability (Creative Glass)"
        ) {
          showCustomPromptText(product, "Available in selected areas only");
          actionEle = getCreativeGlassButton(productPurchsability);
        } else if (productPurchsability && productPurchsability === "Enquire") {
          actionEle = getEnquireOnlyButton(
            getProductUrl(product),
            productPurchsability,
          );
        } else if (
          productPurchsability &&
          productPurchsability === "IN_STORE_ONLY"
        ) {
          actionEle = getInstoreOnlyButton(productPurchsability);
        } else if (productPurchsability && productPurchsability === "Add") {
          const attributes = getCartButtonAttributes(product);

          if (attributes) {
            actionEle = getCartButton(attributes, productPurchsability);
            if (actionEle) {
              dispatchAnalyticsOnClick(actionEle, product);
            }
          }
        } else if (
          productPurchsability &&
          productPurchsability === "Shop in maX"
        ) {
          if (userType === "ANONYMOUS_USER") {
            showTradeAccountHoldersOnlyText(product);
            actionEle = getShopInMaxButton(
              getSecureUrl(product),
              productPurchsability,
            );
          } else {
            showTradeAccountHoldersOnlyText(product);
            actionEle = getTradeAccountOnlyButton();
          }
        } else {
          showTradeAccountHoldersOnlyText(product);
          actionEle = getShopInMaxButton(
            getSecureUrl(product),
            productPurchsability,
          );
        }
      }

      // Override prompt if custom order message was supplied
      if (product.dataset.customOrder) {
        showCustomPromptText(
          product,
          "Delivery Estimate: " + product.dataset.customOrder,
        );
      }

      if (actionEle) {
        setCartActionElement(product, actionEle);
      }
    });
  } catch (e) {
    console.error(e);
  }
};

export const enableProductButtons = (win: WindowProxy) => {
  try {
    const allProducts = [...win.document.querySelectorAll(`.product-tile`)];

    const productsWithAddButton = allProducts.filter((productTile) =>
      productTile.querySelector(`.add-to-cart-component__action-btn--add`),
    );

    productsWithAddButton.forEach((product) => {
      setButtonEnabledState(product, "1");
    });
  } catch (e) {
    console.error(e);
  }
};

export const updateProductButtonStates = (win: WindowProxy) => {
  try {
    const products = [...win.document.querySelectorAll(`.product-tile`)];

    products.forEach((product: HTMLElement) => {
      const addButton = product.querySelector(`add-to-cart`);
      if (addButton) {
        setButtonEnabledState(product, "1");
      } else {
        console.log(
          "updateProductButtonStates: No add button found for product",
          product.dataset.productCode,
        );
      }
    });
  } catch (e) {
    console.error("updateProductButtonStates: Error", e);
  }
};
